import React from "react";
import {
  FormControl,
  Select,
  MenuItem,
  makeStyles,
  Box,
  Typography,
  InputLabel,
} from "@material-ui/core";
import LayersIcon from "@material-ui/icons/Layers";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";

const useStyles = (clientColor) =>
  makeStyles((theme) => ({
    ...customSelectStyle(theme, clientColor),
    sectionContainer: {
      border: `1px solid ${grayColor[8]}`,
      borderLeft: `4px solid ${clientColor || theme.palette.primary.main}`,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
      backgroundColor: grayColor[12],
      position: "relative",
    },
    sectionTitle: {
      fontSize: "0.95rem",
      fontWeight: 500,
      marginBottom: theme.spacing(2),
      color: clientColor || theme.palette.primary.main,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    sectionIcon: {
      fontSize: "1rem",
      opacity: 0.7,
      color: clientColor || theme.palette.primary.main,
    },
    contentRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(2),
      flexWrap: "wrap",
    },
    selectorControl: {
      minWidth: 170,
      flex: 1,
      "& > div": {
        "&:before": {
          borderBottomWidth: "1px !important",
          borderBottomColor: `${grayColor[4]}!important`,
        },
        "&:after": {
          borderBottomColor: `${clientColor}!important`,
        },
      },
      "& .MuiInputLabel-root": {
        "&.Mui-focused": {
          color: `${clientColor}!important`,
        },
      },
    },
  }));

export default function SectionModels({
  variantsModel01,
  selectedVariantModel01,
  onVariantSelectModel01,
  labelModel01,
  variantsModel02,
  selectedVariantModel02,
  onVariantSelectModel02,
  labelModel02,
  clientColor,
}) {
  const classes = useStyles(clientColor)();
  const T_PREFIX = "rgm.cockpit.scenarioCreation";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  return (
    <Box className={classes.sectionContainer}>
      {/* Section Title */}
      <Typography className={classes.sectionTitle}>
        <LayersIcon className={classes.sectionIcon} />
        {t("model_variants")}
      </Typography>

      {/* Dropdowns */}
      <Box className={classes.contentRow}>
        {/* Model01 Variant */}
        <FormControl className={classes.selectorControl}>
          <InputLabel>{labelModel01}</InputLabel>
          <Select
            value={selectedVariantModel01 || ""}
            onChange={(e) => onVariantSelectModel01(e.target.value)}
            MenuProps={{
              className: classes.selectMenu,
              PaperProps: {
                className: classes.selectPaper,
                style: { maxHeight: 300 },
              },
            }}
          >
            {variantsModel01 && variantsModel01.length > 0 ? (
              variantsModel01.map((variant) => (
                <MenuItem
                  key={variant.variant_id}
                  value={variant.variant_id}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {variant.variant_name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>{t("no_variants_available")}</MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Model02 Variant */}
        <FormControl className={classes.selectorControl}>
          <InputLabel>{labelModel02}</InputLabel>
          <Select
            value={selectedVariantModel02 || ""}
            onChange={(e) => onVariantSelectModel02(e.target.value)}
            MenuProps={{
              className: classes.selectMenu,
              PaperProps: {
                className: classes.selectPaper,
                style: { maxHeight: 300 },
              },
            }}
          >
            {variantsModel02 && variantsModel02.length > 0 ? (
              variantsModel02.map((variant) => (
                <MenuItem
                  key={variant.variant_id}
                  value={variant.variant_id}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {variant.variant_name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>{t("no_variants_available")}</MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}

SectionModels.propTypes = {
  variantsModel01: PropTypes.array,
  selectedVariantModel01: PropTypes.number,
  onVariantSelectModel01: PropTypes.func.isRequired,
  labelModel01: PropTypes.string.isRequired,
  variantsModel02: PropTypes.array,
  selectedVariantModel02: PropTypes.number,
  onVariantSelectModel02: PropTypes.func.isRequired,
  labelModel02: PropTypes.string.isRequired,
  clientColor: PropTypes.string,
};
